import React, { useCallback } from "react";
import { type AgGridReact, Button, Flex } from "@jsq/ds/core";
import { MoneyType } from "../../Money/types";
import { exportToolPanel } from "./styles_vanilla.css";
import { PaymentRowItem } from "./types";
import { usePaymentBatchesCsvExport } from "./usePaymentBatchesCsvExport";

export type ExportToolPanelProps = {
  /** Whether the consuming app is GPx (controls Client-column exclusion). */
  isGPX: boolean;
  /** Forwarded to the export filter; matches the table's M3 flag. */
  isM3Enabled?: boolean;
  /**
   * Stable holder of the table's amount reference. The export reads
   * `.amountRef` at click-time to convert the `paymentAmount` filter to minor
   * units with the correct precision so the export honors the table's
   * amount-range filtering.
   */
  amountRefPreserver: { readonly amountRef?: MoneyType };
} & Pick<AgGridReact.CustomToolPanelProps<PaymentRowItem>, "api">;

/**
 * AG-Grid sidebar tool panel that triggers a CSV export of the current
 * payments list. Reads filter/sort state from the live `GridApi` provided by
 * AG-Grid as `props.api`, so exports always match what the user sees in the
 * table at click-time.
 */
export const ExportToolPanel: React.FC<ExportToolPanelProps> = ({
  api,
  isGPX,
  isM3Enabled,
  amountRefPreserver,
}) => {
  const getGridState = useCallback(() => api ?? null, [api]);
  const { exportToCsv, isExporting } = usePaymentBatchesCsvExport({
    getGridState,
    amountRefPreserver,
    isGPX,
    isM3Enabled,
  });
  const handleClick = useCallback(() => void exportToCsv(), [exportToCsv]);

  return (
    <Flex className={exportToolPanel}>
      <Button
        variant="neutral"
        icon="download"
        iconPosition="left"
        onClick={handleClick}
        isLoading={isExporting}
        disabled={isExporting}
      >
        Export to CSV
      </Button>
    </Flex>
  );
};
